import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBalanceScale,
  faCheckCircle,
  faExclamationTriangle,
  faCalendar,
  faDollarSign,
  faFileInvoiceDollar,
  faChartLine,
  faDatabase,
  faSync,
  faRobot,
  faLightbulb,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { costService } from '../services/costService';

interface ReconciliationTabProps {
  data: any;
  loading?: boolean;
  customCosts?: number;
  customEntries?: any[];
  uploadedBills?: Bill[];
}

interface Bill {
  id: string;
  month: string;
  provider: string;
  accountName: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue';
  dueDate: string;
  paidDate?: string;
  invoiceNumber?: string;
  currency: string;
  fileName?: string;
  fileType?: string;
}

interface MonthlyReconciliation {
  month: string;
  year: number;
  connectedAccountsCosts: {
    aws: number;
    gcp: number;
    github: number;
    total: number;
  };
  customDataEntered: number;
  customDataBreakdown?: { vendor: string; amount: number }[];
  uploadedInvoicesTotal: number;
  totalExpectedCost: number;
  discrepancy: number;
  discrepancyPercentage: number;
  status: 'reconciled' | 'discrepancy' | 'missing-invoices';
  uploadedBills: Bill[];
}


const getStatusColor = (status: string) => {
  switch (status) {
    case 'reconciled': return 'text-green-600';
    case 'discrepancy': return 'text-yellow-600';
    case 'missing-invoices': return 'text-red-600';
    default: return 'text-gray-600';
  }
};

const getStatusBg = (status: string) => {
  switch (status) {
    case 'reconciled': return 'bg-green-50 border-green-200';
    case 'discrepancy': return 'bg-yellow-50 border-yellow-200';
    case 'missing-invoices': return 'bg-red-50 border-red-200';
    default: return 'bg-gray-50 border-gray-300';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'reconciled': return faCheckCircle;
    case 'discrepancy': return faExclamationTriangle;
    case 'missing-invoices': return faExclamationTriangle;
    default: return faBalanceScale;
  }
};

const getProviderIcon = (provider: string) => {
  switch (provider) {
    case 'aws': return faAws;
    case 'gcp': return faGoogle;
    case 'custom': return faDatabase;
    default: return faDollarSign;
  }
};

interface LLMAnalysis {
  summary: string;
  findings: string[];
  recommendations: string[];
  confidence: number;
  calculationsVerified: boolean;
  errors: string[];
}

export const ReconciliationTab: React.FC<ReconciliationTabProps> = ({
  data,
  loading = false,
  customCosts = 0,
  customEntries = [],
  uploadedBills = []
}) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<MonthlyReconciliation | null>(null);
  const [reconciliationData, setReconciliationData] = useState<MonthlyReconciliation[]>([]);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [reconciliationError, setReconciliationError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [llmAnalysis, setLlmAnalysis] = useState<LLMAnalysis | null>(null);
  const [showLLMAnalysis, setShowLLMAnalysis] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Pull reconciliation totals (AWS + GCP + Custom vs Bills) from the
  // /api/cost/reconciliation endpoint; same shape as the local interface.
  const loadReconciliation = useCallback(async (year: number) => {
    setReconciliationLoading(true);
    setReconciliationError(null);
    try {
      const result = await costService.getReconciliation(year);
      setReconciliationData((result.data || []) as MonthlyReconciliation[]);
    } catch (err: any) {
      console.error('Failed to load reconciliation:', err);
      setReconciliationError(err?.message || 'Failed to load reconciliation');
      setReconciliationData([]);
    } finally {
      setReconciliationLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReconciliation(selectedYear);
  }, [selectedYear, loadReconciliation]);

  const handleMonthSelect = (month: MonthlyReconciliation) => {
    setSelectedMonth(month);
  };

  // Sync = re-pull totals from backend, then ask the LLM for an analysis
  // over those totals. Refresh-then-analyse keeps the AI commenting on the
  // same numbers the user is looking at.
  const handleSync = async () => {
    setSyncing(true);
    setShowLLMAnalysis(false);

    try {
      await loadReconciliation(selectedYear);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/api/cost/reconciliation/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          connectedAccountsData: data,
          customCosts: customEntries,
          uploadedBills: uploadedBills,
          reconciliationData: reconciliationData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch LLM analysis');
      }

      const result = await response.json();
      if (result.updatedReconciliationData) {
        setReconciliationData(result.updatedReconciliationData);
      }
      setLlmAnalysis(result.llmAnalysis);
      setShowLLMAnalysis(true);
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Error syncing reconciliation data:', error);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded p-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xs font-semibold text-gray-900 mb-1 flex items-center">
              <FontAwesomeIcon icon={faBalanceScale} className="text-purple-600 mr-2" />
              Cost Reconciliation
              <FontAwesomeIcon icon={faRobot} className="text-blue-600 ml-2 text-[10px]" title="AI-Powered Analysis" />
            </h2>
            <p className="text-xs text-gray-600">
              Compare connected account costs + custom data against uploaded invoices to identify discrepancies
            </p>
            <div className="flex items-center space-x-3 mt-1 text-[10px] text-gray-500">
              {lastSyncTime && <span>Last synced: {lastSyncTime.toLocaleString()}</span>}
              {reconciliationLoading && (
                <span className="flex items-center text-purple-700">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                  Loading reconciliation...
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              title="Year"
            >
              {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={handleSync}
              disabled={syncing || loading || reconciliationLoading}
              className={`px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2 ${syncing ? 'animate-pulse' : ''}`}
            >
              <FontAwesomeIcon icon={faSync} className={`text-xs ${syncing ? 'animate-spin' : ''}`} />
              <span>{syncing ? 'Syncing...' : 'Sync & Analyze'}</span>
            </button>
            {llmAnalysis && (
              <button
                onClick={() => setShowLLMAnalysis(!showLLMAnalysis)}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-all flex items-center space-x-2"
              >
                <FontAwesomeIcon icon={faLightbulb} className="text-xs" />
                <span>{showLLMAnalysis ? 'Hide' : 'Show'} AI Insights</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {reconciliationError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-3">
          {reconciliationError}
        </div>
      )}

      {/* LLM Analysis Panel */}
      {showLLMAnalysis && llmAnalysis && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded p-3 shadow-lg">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faRobot} className="text-blue-600 text-sm" />
              <h3 className="text-xs font-semibold text-gray-900">AI-Powered Reconciliation Analysis</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                llmAnalysis.calculationsVerified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {llmAnalysis.calculationsVerified ? 'Verified ✓' : 'Review Needed'}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                {Math.round(llmAnalysis.confidence * 100)}% Confidence
              </span>
            </div>
            <button
              onClick={() => setShowLLMAnalysis(false)}
              className="text-gray-400 hover:text-gray-600 text-xs"
            >
              ✕
            </button>
          </div>

          {/* Summary */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-900 mb-2">Summary</h4>
            <p className="text-xs text-gray-700 leading-relaxed bg-white rounded p-3 border border-blue-200">
              {llmAnalysis.summary}
            </p>
          </div>

          {/* Key Findings */}
          {llmAnalysis.findings.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mr-2 text-xs" />
                Key Findings ({llmAnalysis.findings.length})
              </h4>
              <ul className="space-y-2">
                {llmAnalysis.findings.map((finding, index) => (
                  <li key={index} className="text-xs text-gray-700 bg-white rounded p-2 border border-yellow-200 flex items-start">
                    <span className="text-yellow-600 mr-2 font-bold">•</span>
                    <span>{finding}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Errors */}
          {llmAnalysis.errors.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-red-900 mb-2 flex items-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2 text-xs" />
                Errors Detected ({llmAnalysis.errors.length})
              </h4>
              <ul className="space-y-2">
                {llmAnalysis.errors.map((error, index) => (
                  <li key={index} className="text-xs text-red-700 bg-red-50 rounded p-2 border border-red-300 flex items-start">
                    <span className="text-red-600 mr-2 font-bold">✗</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {llmAnalysis.recommendations.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="text-green-600 mr-2 text-xs" />
                Recommendations ({llmAnalysis.recommendations.length})
              </h4>
              <ul className="space-y-2">
                {llmAnalysis.recommendations.map((recommendation, index) => (
                  <li key={index} className="text-xs text-gray-700 bg-white rounded p-2 border border-green-200 flex items-start">
                    <span className="text-green-600 mr-2 font-bold">→</span>
                    <span>{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600 mb-1">Total Months Tracked</div>
              <div className="text-xs font-semibold text-gray-900">
                {reconciliationData.length}
              </div>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded flex items-center justify-center">
              <FontAwesomeIcon icon={faCalendar} className="text-purple-600 text-xs" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600 mb-1">Reconciled Months</div>
              <div className="text-xs font-semibold text-green-600">
                {reconciliationData.filter(m => m.status === 'reconciled').length}
              </div>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-xs" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600 mb-1">With Discrepancies</div>
              <div className="text-xs font-semibold text-yellow-600">
                {reconciliationData.filter(m => m.status !== 'reconciled').length}
              </div>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded flex items-center justify-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-xs" />
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Reconciliation List */}
      <div className="bg-white border border-gray-300 rounded overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-300 bg-gray-50">
          <h3 className="text-xs font-semibold text-gray-900">Monthly Reconciliation Status</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {reconciliationData.map((month, index) => (
            <div
              key={index}
              className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => handleMonthSelect(month)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="flex items-center space-x-3">
                    <FontAwesomeIcon icon={faCalendar} className="text-gray-400 text-xs" />
                    <div>
                      <div className="text-xs font-medium text-gray-900">
                        {month.month} {month.year}
                      </div>
                      <div className="text-[10px] text-gray-600">
                        {month.uploadedBills.length} invoices uploaded
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6 ml-auto">
                    <div className="text-right">
                      <div className="text-[10px] text-gray-600">Expected Cost</div>
                      <div className="text-xs font-medium text-gray-900">
                        ${month.totalExpectedCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-[10px] text-gray-600">Uploaded Invoices</div>
                      <div className="text-xs font-medium text-gray-900">
                        ${month.uploadedInvoicesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-[10px] text-gray-600">Discrepancy</div>
                      <div className={`text-xs font-semibold ${month.discrepancy === 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {month.discrepancy > 0 ? '+' : ''}{month.discrepancy === 0 ? '✓ ' : ''}
                        ${Math.abs(month.discrepancy).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    <div className={`px-3 py-1 rounded-full border ${getStatusBg(month.status)}`}>
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon
                          icon={getStatusIcon(month.status)}
                          className={`${getStatusColor(month.status)} text-xs`}
                        />
                        <span className={`text-[10px] font-medium ${getStatusColor(month.status)}`}>
                          {month.status === 'reconciled' ? 'Reconciled' :
                           month.status === 'discrepancy' ? 'Minor Discrepancy' :
                           'Missing Invoices'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Reconciliation View */}
      {selectedMonth && (
        <div className={`border rounded overflow-hidden ${getStatusBg(selectedMonth.status)}`}>
          <div className="px-4 py-3 border-b border-gray-300">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-900">
                Detailed Reconciliation: {selectedMonth.month} {selectedMonth.year}
              </h3>
              <button
                onClick={() => setSelectedMonth(null)}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Close
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Cost Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-gray-300 rounded p-3">
                <div className="text-[10px] text-gray-600 mb-2">Connected Accounts (Usage-Based)</div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">AWS</span>
                    <span className="font-medium text-gray-900">
                      ${selectedMonth.connectedAccountsCosts.aws.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">GCP</span>
                    <span className="font-medium text-gray-900">
                      ${selectedMonth.connectedAccountsCosts.gcp.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">GitHub</span>
                    <span className="font-medium text-gray-900">
                      ${selectedMonth.connectedAccountsCosts.github.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-300">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-gray-900">Subtotal</span>
                      <span className="font-semibold text-gray-900">
                        ${selectedMonth.connectedAccountsCosts.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-300 rounded p-3">
                <div className="text-[10px] text-gray-600 mb-2">Custom Data Entered</div>
                <div className="space-y-1">
                  {selectedMonth.customDataBreakdown && selectedMonth.customDataBreakdown.length > 0 ? (
                    selectedMonth.customDataBreakdown.map((item, i) => (
                      <div key={`${item.vendor}-${i}`} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{item.vendor}</span>
                        <span className="font-medium text-gray-900">
                          ${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No custom entries for this month</div>
                  )}
                  <div className="pt-2 mt-2 border-t border-gray-300">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-gray-900">Subtotal</span>
                      <span className="font-semibold text-gray-900">
                        ${selectedMonth.customDataEntered.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-300 rounded p-3">
                <div className="text-[10px] text-gray-600 mb-2">Total Expected Cost</div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Connected Accounts</span>
                    <span className="font-medium text-gray-900">
                      ${selectedMonth.connectedAccountsCosts.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Custom Data</span>
                    <span className="font-medium text-gray-900">
                      ${selectedMonth.customDataEntered.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-300">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-gray-900">Total Expected</span>
                      <span className="font-bold text-gray-900 text-sm">
                        ${selectedMonth.totalExpectedCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Uploaded Invoices */}
            <div className="bg-white border border-gray-300 rounded overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-300 bg-gray-50">
                <h4 className="text-xs font-semibold text-gray-900">Uploaded Invoices ({selectedMonth.uploadedBills.length})</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                      <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Account</th>
                      <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                      <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-3 py-2 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedMonth.uploadedBills.map((bill) => (
                      <tr key={bill.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={getProviderIcon(bill.provider)}
                              className="text-gray-600 text-xs mr-2"
                            />
                            <span className="text-xs text-gray-900">{bill.provider.toUpperCase()}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{bill.accountName}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{bill.invoiceNumber}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                          ${bill.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-medium ${
                            bill.status === 'paid' ? 'bg-green-100 text-green-700' :
                            bill.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {bill.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={3} className="px-3 py-2 text-xs text-gray-900">Total Uploaded Invoices</td>
                      <td className="px-3 py-2 text-right text-xs text-gray-900">
                        ${selectedMonth.uploadedInvoicesTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Reconciliation Result */}
            <div className={`border-2 rounded p-3 ${
              selectedMonth.discrepancy === 0 ? 'border-green-300 bg-green-50' :
              Math.abs(selectedMonth.discrepancy) < 100 ? 'border-yellow-300 bg-yellow-50' :
              'border-red-300 bg-red-50'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold text-gray-900 mb-1">
                    Reconciliation Result
                  </div>
                  <div className="text-[10px] text-gray-600">
                    {selectedMonth.discrepancy === 0 ? '✓ All costs accounted for' :
                     selectedMonth.discrepancy > 0 ? `Missing invoices worth $${Math.abs(selectedMonth.discrepancy).toFixed(2)}` :
                     `Over-billed by $${Math.abs(selectedMonth.discrepancy).toFixed(2)}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-600 mb-1">Discrepancy</div>
                  <div className={`text-lg font-bold ${
                    selectedMonth.discrepancy === 0 ? 'text-green-600' :
                    Math.abs(selectedMonth.discrepancy) < 100 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {selectedMonth.discrepancy === 0 ? '✓' :
                     selectedMonth.discrepancy > 0 ? '+' : '-'}
                    ${Math.abs(selectedMonth.discrepancy).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-[10px] text-gray-600">
                    ({selectedMonth.discrepancyPercentage.toFixed(1)}%)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
